/* Canonical money rule (frontend preview totals).
 *
 * Plan §"Canonical amount": the same formula must be used by Phase B server
 * recomputation in `up-backend/src/application/use-cases/customer-checkout/`
 * (today: `session.amount = input.amountMinor / 100`). When the backend
 * extends the create-session use case to derive `amountMinor` from line
 * items, it MUST round identically:
 *
 *   amountMinor = Math.round(amountMajor * 100)
 *   amountMajor = amountMinor / 100
 *
 * W17: charge/format follow checkout session currency via resolveShopCurrency.
 * SHOP_CURRENCY remains a deprecated fallback alias for pre-session call sites.
 */

import type { CurrencyCode } from 'pi-kiosk-shared';
import { isCurrencyCode } from 'pi-kiosk-shared';
import i18n from '../../../i18n.js';
import { resolveCustomerLocaleTag } from '../../../shared/hooks/useCustomerLocaleTag.js';

/** Fallback when session currency is unavailable (pre-picker / legacy). */
export const SHOP_CURRENCY_FALLBACK = 'CZK' as const satisfies CurrencyCode;

/**
 * @deprecated Prefer {@link resolveShopCurrency} once session currency is wired.
 * Kept as the runtime fallback constant for call sites not yet session-driven.
 */
export const SHOP_CURRENCY = SHOP_CURRENCY_FALLBACK;
export type ShopCurrency = CurrencyCode;

/** Resolve shop charge/format currency from checkout session (W17). */
export function resolveShopCurrency(
  sessionCurrency: string | null | undefined,
): CurrencyCode {
  return isCurrencyCode(sessionCurrency) ? sessionCurrency : SHOP_CURRENCY_FALLBACK;
}

export function toMinorUnits(amountMajor: number): number {
  if (!Number.isFinite(amountMajor)) {
    return 0;
  }
  return Math.round(amountMajor * 100);
}

export function toMajorUnits(amountMinor: number): number {
  if (!Number.isFinite(amountMinor)) {
    return 0;
  }
  return amountMinor / 100;
}

export function formatPrice(
  amountMajor: number,
  currency: string = SHOP_CURRENCY_FALLBACK,
  localeTag?: string
): string {
  const safe = Number.isFinite(amountMajor) ? amountMajor : 0;
  const resolvedCurrency = resolveShopCurrency(currency);
  const resolvedLocale =
    localeTag ?? resolveCustomerLocaleTag(i18n.resolvedLanguage ?? i18n.language);
  return new Intl.NumberFormat(resolvedLocale, {
    style: 'currency',
    currency: resolvedCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safe);
}
